import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

const PropertyIcon = RESIDENTS_SVG_COMPONENTS.PROPERTY;
const GroupIcon = RESIDENTS_SVG_COMPONENTS.GROUP;

export interface ChatGlassConversationCardProps {
  id: number | string;
  subject: string;
  preview: string;
  date: string;
  isGroup?: boolean;
  recipientCount?: number;
  hasUnread?: boolean;
  isSelected?: boolean;
  thumbnail?: string;
  subtitle?: string;
  onPress: (id: number | string) => void;
}

export const ChatGlassConversationCard: React.FC<ChatGlassConversationCardProps> = React.memo(({
  id,
  subject,
  preview,
  date,
  isGroup = false,
  recipientCount,
  hasUnread = false,
  isSelected = false,
  thumbnail,
  subtitle,
  onPress,
}) => {
  const { colors } = useThemeColors();
  const Icon = isGroup ? GroupIcon : PropertyIcon;
  const isMarketplace = !!thumbnail;

  return (
    <TouchableOpacity
      style={[
        styles.container,
        { borderBottomColor: colors.separator },
        hasUnread && { backgroundColor: colors.glass.buttonBg },
        isSelected && { backgroundColor: colors.accent.blueBgLight },
      ]}
      onPress={() => onPress(id)}
      activeOpacity={0.7}
    >
      {isMarketplace ? (
        <Image
          source={{ uri: thumbnail }}
          style={[styles.thumbnailAvatar, { backgroundColor: colors.activityIcon.bg }]}
          resizeMode="cover"
        />
      ) : (
        <View
          style={[
            styles.avatar,
            { backgroundColor: colors.activityIcon.bg, borderColor: colors.activityIcon.border },
          ]}
        >
          <Icon
            width={isGroup ? 20 : 16}
            height={isGroup ? 20 : 16}
            color={colors.text.primary}
          />
        </View>
      )}

      <View style={styles.content}>
        <View style={styles.topRow}>
          <Text style={[styles.subject, { color: colors.text.primary }]} numberOfLines={1}>
            {subject}
          </Text>
          <Text style={[styles.date, { color: colors.text.subtle }]}>{date}</Text>
        </View>

        <View style={styles.bottomRow}>
          <Text style={[styles.preview, { color: colors.text.muted }]} numberOfLines={1}>
            {preview}
          </Text>
          {hasUnread && (
            <LinearGradient
              colors={['#F5AF19', '#F48D17', '#F36B15', '#F24913', '#F12711']}
              style={styles.unreadIndicator}
            />
          )}
        </View>

        {subtitle ? (
          <Text style={[styles.meta, { color: colors.text.subtle }]} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : isGroup && recipientCount !== undefined && recipientCount > 0 ? (
          <Text style={[styles.meta, { color: colors.text.subtle }]}>
            {recipientCount} Recipient{recipientCount !== 1 ? 's' : ''}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
});

ChatGlassConversationCard.displayName = 'ChatGlassConversationCard';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingVertical: 14,
    gap: 16,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  content: {
    flex: 1,
    gap: 4,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  subject: {
    flex: 1,
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  date: {
    ...REDESIGN_TYPOGRAPHY.caption,
    textAlign: 'right',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  preview: {
    flex: 1,
    ...REDESIGN_TYPOGRAPHY.body,
  },
  unreadIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  meta: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
});
